import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useSearchParams } from 'react-router-dom';
import { getContextValue, isDeployedInChina } from '../utils/globalContext';
import {
  getDictionary,
  availableLocales,
  languageToLocale,
} from './staticFiles';
import { getDefaultLocaleForCluster, getLocalizedValue } from './translations';

export const getLocaleFromCookie = (cookies) => {
  const cookie = cookies['ln-ma'] || '';
  const [languageCookie, marketCookie] = cookie.split('-');

  if (languageCookie && marketCookie) {
    return languageCookie.toLowerCase() + '-' + marketCookie.toUpperCase();
  }

  return '';
};

export const getLocaleFromQueryParams = (searchParams) => {
  const languageQuery = searchParams.get('language');
  const marketQuery = searchParams.get('market');

  if (languageQuery && marketQuery) {
    return languageQuery.toLowerCase() + '-' + marketQuery.toUpperCase();
  }

  return '';
};

export const getUiLocales = (cookies, searchParams) => {
  const searchParam = searchParams.get('ui_locales');

  if (searchParam) {
    return searchParam.split(' ');
  }

  const cookie = cookies['ui_locales'] || '';
  return cookie.split(' ') || [];
};

export const getLanguageAndMarket = (cookies, searchParams = '') => {
  if (isDeployedInChina) {
    return {
      language: 'zh',
      market: 'CN',
    };
  }

  const marketQuery = searchParams.get('market');
  const languageQuery = searchParams.get('language');

  if (marketQuery && languageQuery) {
    return {
      language: languageQuery.toLowerCase(),
      market: marketQuery.toUpperCase(),
    };
  }

  const cookie = cookies['ln-ma'] || '';
  const [languageCookie, marketCookie] = cookie.split('-');

  if (marketCookie && languageCookie !== '') {
    return {
      language: languageCookie.toLowerCase(),
      market: marketCookie.toUpperCase(),
    };
  }

  return {
    language: 'en',
    market: 'GB',
  };
};

const getSupportedLocale = (localeProp) => {
  if (availableLocales.includes(localeProp)) {
    return localeProp;
  }
  const language = localeProp.split('-')[0];
  const mappedLocale = languageToLocale[language];

  if (availableLocales.includes(mappedLocale)) {
    return mappedLocale;
  }

  const substituteLocale = availableLocales.find((key = '') => {
    const languageFromKey = key.split('-')[0];
    return language === languageFromKey;
  });

  if (substituteLocale) {
    return substituteLocale;
  }
};

export function useLocale() {
  const [cookies] = useCookies(['ln-ma']);
  let [searchParams] = useSearchParams();
  const localeFromQueryParams = getLocaleFromQueryParams(searchParams);
  const localeFromCookie = getLocaleFromCookie(cookies);
  const uiLocales = getUiLocales(cookies, searchParams);

  if (localeFromQueryParams) {
    const locale = getSupportedLocale(localeFromQueryParams);
    if (locale) {
      return locale;
    }
  }

  for (let i = 0; i < uiLocales.length; i++) {
    const locale = getSupportedLocale(uiLocales[i]);
    if (locale) {
      return locale;
    }
  }

  if (localeFromCookie) {
    const locale = getSupportedLocale(localeFromCookie);
    if (locale) {
      return locale;
    }
  }

  return getDefaultLocaleForCluster(getContextValue('REACT_APP_CLUSTER'));
}

export function useLanguageAndMarket() {
  const [cookies] = useCookies(['ln-ma']);
  let [searchParams] = useSearchParams();

  return getLanguageAndMarket(cookies, searchParams);
}

export function useDictionary() {
  const [dictionary, setDictionary] = useState({});
  const locale = useLocale();

  useEffect(() => {
    getDictionary(locale).then((dictionaryByLocale) => {
      if (dictionaryByLocale) {
        setDictionary(dictionaryByLocale);
      }
    });
  }, [locale]);

  return dictionary;
}

export function useTranslate() {
  const dictionary = useDictionary();

  return {
    translate: (key) => getLocalizedValue(dictionary, key),
  };
}

export function isRtl(locale = '') {
  const rtlLanguages = ['ar', 'he'];

  return (
    rtlLanguages.filter((current) => locale.toLowerCase().startsWith(current))
      .length > 0
  );
}
